import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import Select from "react-select";
import { fetchEquipoLookups } from "../../../../../../features/sg/equipos";
import type { Device, ThemeClasses, EditFormType, Vehicle, Brand } from "../types";
import { formatDate } from "../utils/formatters";
import Modal from "../../../../../../components/base/Modal";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { useRealtimeDeviceData } from "../../../../../../store/admin/vehiclesSocketSelector";
import { useVehiclesSocketStore } from "../../../../../../store/admin/vehiclesSocketStore";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { getCustomSelectStyles } from "../../../../shared";
import useIsReseller from "../../../../../../hooks/useIsReseller";
import { useSchemaOverrides } from "../../../../../../hooks/useSchemaOverrides";
import type { TFunction } from "i18next";
import BrandModel from "./BrandModel";
import { fuelSensorErrors, fuelSensorPayload, rawValue } from "../utils/fuelSensor";
import { PANIC_BUTTON_OPTIONS, parsePanicButton } from "../utils/panicButton";

interface DeviceEditModalProps {
  device: Device;
  editForm: Partial<EditFormType>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  availableVehicles: Vehicle[];
  availableVehiclesLoading?: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSave: (data: Partial<EditFormType>) => void;
  onInputChange: (field: string, value: any) => void;
  onRefreshVehicles?: () => void;
  allBrands: Brand[];
  allBrandsLoading: boolean;
  t: (key: string, options?: any) => string;
  isSgLinked?: boolean;
}

export function DeviceEditModal({
  device,
  editForm,
  themeClasses,
  isDarkMode,
  availableVehicles,
  availableVehiclesLoading = false,
  isSubmitting,
  onClose,
  onSave,
  allBrands,
  allBrandsLoading,
  t,
  isSgLinked = false,
}: DeviceEditModalProps) {
  const disableCRUD = useDisableCRUD();
  const { isVisible, isRequired } = useSchemaOverrides("devices", "putDevice");

  const realtime = useRealtimeDeviceData(device.id);
  const requestAddressForDevice = useVehiclesSocketStore((state) => state.requestAddressForDevice);
  const loadingAddress = useVehiclesSocketStore((state) => !!state.loadingAddressDeviceIds[device.id]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    getValues,
  } = useForm<Partial<EditFormType>>({
    defaultValues: editForm,
  });

  // Último valor crudo del parámetro del sensor, de la última posición del
  // equipo (socketParams = last_position.params): ayuda a calibrar vacío/lleno.
  const fuelParam = watch("fuel_sensor.param");
  const lastRaw = useVehiclesSocketStore((s) => {
    if (!fuelParam) return undefined;
    const i = s.vehiclesMap.get(device.id);
    return i === undefined ? undefined : s.vehicles[i]?.socketParams?.[fuelParam];
  });

  // Observar el brand_id seleccionado
  const selectedBrandId = watch("brand_id");
  const selectedBrand = allBrands.find((b) => b.id === selectedBrandId);

  useEffect(() => {
    if (device.id) {
      requestAddressForDevice(device.id);
    }
  }, [device.id, requestAddressForDevice]);

  // Reset form when editForm changes
  useEffect(() => {
    reset(editForm);
  }, [editForm, reset]);

  const getInputClassName = (hasError: boolean) =>
    `w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${hasError ? "border-red-500" : themeClasses.cardBorder
    } rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 ${hasError ? "focus:ring-red-500" : "focus:ring-primary-500"
    }`;

  const errorClassName = "text-red-500 text-xs mt-1";

  // Opciones de estado filtradas para devices
  const statuses = useStatusesStore((state) => state.statuses);
  const statusOptionsSelect = getStatusOptionsForEntity(statuses, "devices", t as TFunction<"translation", undefined>);

  const { data: sgEquipoLookups } = useQuery({
    queryKey: ["sg-equipo-lookups"],
    queryFn: fetchEquipoLookups,
    staleTime: 5 * 60 * 1000,
  });
  const watchedStatusId = watch("status_id");
  const sgConditionOptions = (() => {
    const sgStatuses = sgEquipoLookups?.statuses ?? [];
    const conditions = sgEquipoLookups?.conditions ?? [];
    const active = watchedStatusId === "R5tW0uX4yY9cF6dH2aI8";
    const sgStatus = sgStatuses.find(
      (s: any) =>
        (s.name_es || s.name || "").toLowerCase() ===
        (active ? "activo" : "inactivo"),
    );
    return conditions
      .filter((c: any) => !sgStatus || String(c.status_id) === String(sgStatus.id))
      .map((c: any) => ({ value: c.name, label: c.name }));
  })();

  const typeOptions = [
    { value: "gps", label: t("devices.type.gps") },
    { value: "phone", label: t("devices.type.phone") },
    { value: "bluetooth", label: t("devices.type.bluetooth") },
    { value: "internal", label: t("devices.type.internal") },
  ];

  const brandOptions = allBrands.map((brand) => ({
    value: brand.id,
    label: `${brand.brand} - ${brand.model} - ${brand.origin?.toUpperCase()}`,
  }));

  const baseVehicleOptions = availableVehicles.map((vehicle) => ({
    value: vehicle.id,
    label: `${vehicle.name} - ${vehicle.plate || t("devices.messages.noPlate")}`,
  }));

  const vehicleOptions = device.vehicle && device.vehicle_id
    ? [
      ...(baseVehicleOptions.some(opt => opt.value === device.vehicle_id)
        ? []
        : [{
          value: device.vehicle_id,
          label: `${device.vehicle.name} - ${device.vehicle.plate || t("devices.messages.noPlate")}`,
        }]),
      ...baseVehicleOptions,
    ]
    : baseVehicleOptions;

  const isDumax = import.meta.env.VITE_THEME === "dumax";
  const onSubmit = (data: Partial<EditFormType>) => {
    // Sección vacía: null quita una calibración existente; si no había, no se manda.
    onSave({ ...data, fuel_sensor: fuelSensorPayload(data.fuel_sensor, !!device.fuel_sensor) });
  };

  const { isDisabled } = useIsReseller();

  return (
    <Modal
      width="w-full max-w-3xl"
      content={
        <form
          id="device-edit-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-2 w-full"
        >
          {isSgLinked && (
            <div
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${
                isDarkMode
                  ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                  : "border-amber-300 bg-amber-50 text-amber-800"
              }`}
            >
              <i className="ri-links-line mt-0.5 text-lg"></i>
              <div>
                <p className="text-sm font-semibold">
                  {t("devices.sgNotice.title", { defaultValue: "Compañía ligada a SG" })}
                </p>
                <p className="mt-0.5 text-xs">
                  {t("devices.sgNotice.edit", {
                    defaultValue:
                      "Los cambios de estado y etiqueta de este equipo se sincronizan con SG.",
                  })}
                </p>
              </div>
            </div>
          )}

          {/* Info del dispositivo */}
          <div
            className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
              } p-4 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-4`}
          >
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>ID</p>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {device.id}
              </p>
            </div>
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>{t("devices.fields.createdAt")}</p>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {formatDate(device.createdAt)}
              </p>
            </div>
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>
                {t("devices.fields.updatedAt")}
              </p>
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {formatDate(device.updatedAt)}
              </p>
            </div>
            <div>
              <p className={`font-semibold ${themeClasses.text}`}>
                {t("devices.fields.lastAddress")}
              </p>
              {loadingAddress ? (
                <div className="flex items-center gap-2 mt-1">
                  <div className={`animate-spin h-3 w-3 border-2 border-t-transparent rounded-full ${isDarkMode ? 'border-gray-400' : 'border-gray-500'}`}></div>
                  <span className={`text-sm ${themeClasses.textSecondary}`}>{t("devices.messages.loadingAddress")}</span>
                </div>
              ) : realtime.address ? (
                <p className={`text-sm ${themeClasses.text}`}>
                  {realtime.address}
                </p>
              ) : (
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {t("devices.messages.noAddress")}
                </p>
              )}
            </div>
          </div>

          {/* Sección: Información Básica */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-2">
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("devices.sections.basicInfo")}
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Nombre */}
            {isVisible("name") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.name")} {isRequired("name") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.name)}
                  type="text"
                  placeholder={t("devices.placeholders.name")}
                  {...register("name", {
                    required: isRequired("name") ? t("devices.validation.nameRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.name && (
                  <span className={errorClassName}>{errors.name.message}</span>
                )}
              </div>
            )}

            {/* IMEI/ID */}
            {isVisible("identifier") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  IMEI/ID {isRequired("identifier") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.identifier)}
                  type="text"
                  placeholder={t("devices.placeholders.imei")}
                  {...register("identifier", {
                    required: isRequired("identifier") ? t("devices.validation.imeiRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.identifier && (
                  <span className={errorClassName}>{errors.identifier.message}</span>
                )}
              </div>
            )}

            {/* Serial */}
            {isVisible("serial") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.serial")} {isRequired("serial") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.serial)}
                  type="text"
                  placeholder={t("devices.placeholders.serial")}
                  {...register("serial", {
                    required: isRequired("serial") ? t("devices.validation.serialRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.serial && (
                  <span className={errorClassName}>{errors.serial.message}</span>
                )}
              </div>
            )}

            {/* Tipo */}
            {isVisible("type") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.type")} {isRequired("type") && <span className="text-red-500">*</span>}
                </span>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: isRequired("type") ? t("devices.validation.typeRequired") : false }}
                  render={({ field }) => (
                    <Select
                      options={typeOptions}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.type)}
                      placeholder={t("devices.placeholders.selectType")}
                      value={typeOptions.find((opt) => opt.value === field.value) || null}
                      onChange={(option: any) => field.onChange(option?.value || "")}
                      isSearchable
                      noOptionsMessage={() => t("devices.noOptions.types")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isDisabled={isDisabled}
                    />
                  )}
                />
                {errors.type && (
                  <span className={errorClassName}>{errors.type.message}</span>
                )}
              </div>
            )}

            {/* Estado */}
            {isVisible("status_id") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.status")} {isRequired("status_id") && <span className="text-red-500">*</span>}
                </span>
                <Controller
                  name="status_id"
                  control={control}
                  rules={{ required: isRequired("status_id") ? t("devices.validation.statusRequired") : false }}
                  render={({ field }) => (
                    <Select
                      options={statusOptionsSelect}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.status_id)}
                      placeholder={t("devices.placeholders.selectStatus")}
                      value={statusOptionsSelect.find((opt) => opt.value === field.value) || null}
                      onChange={(option: any) => field.onChange(option?.value || "")}
                      isSearchable
                      noOptionsMessage={() => t("devices.noOptions.statuses")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isDisabled={isDisabled}
                    />
                  )}
                />
                {errors.status_id && (
                  <span className={errorClassName}>{errors.status_id.message}</span>
                )}
              </div>
            )}

            {isSgLinked && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.tag", { defaultValue: "Etiqueta (SG)" })}
                </span>
                <Controller
                  name="sg_condition"
                  control={control}
                  render={({ field }) => {
                    const opts =
                      field.value &&
                      !sgConditionOptions.some((o) => o.value === field.value)
                        ? [...sgConditionOptions, { value: field.value, label: field.value }]
                        : sgConditionOptions;
                    return (
                      <Select
                        options={opts}
                        styles={getCustomSelectStyles(isDarkMode, false)}
                        placeholder={t("devices.placeholders.selectTag", { defaultValue: "Sin etiqueta" })}
                        value={opts.find((o) => o.value === field.value) || null}
                        onChange={(option: any) => field.onChange(option?.value || "")}
                        isClearable
                        noOptionsMessage={() => t("devices.noOptions.statuses")}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    );
                  }}
                />
              </div>
            )}

            {/* Vehículo Asignado - Comentado: la conexión ahora se hace desde Units */}
            {isDumax && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  Vehículo Asignado
                </span>
                <Controller
                  name="vehicle_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={vehicleOptions}
                      styles={getCustomSelectStyles(isDarkMode)}
                      placeholder={availableVehiclesLoading ? t("devices.placeholders.loadingVehicles") : t("devices.placeholders.selectVehicle")}
                      value={vehicleOptions.find((opt) => opt.value === field.value) || null}
                      onChange={(option: any) => field.onChange(option?.value || "")}
                      isSearchable
                      isClearable
                      isLoading={availableVehiclesLoading}
                      isDisabled={availableVehiclesLoading}
                      noOptionsMessage={() => t("devices.noOptions.vehicles")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
              </div>
            )}

            {/* Marca del Dispositivo */}
            {isVisible("brand_id") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.brand")} {isRequired("brand_id") && <span className="text-red-500">*</span>}
                </span>
                <Controller
                  name="brand_id"
                  control={control}
                  rules={{ required: isRequired("brand_id") ? t("devices.validation.brandRequired") : false }}
                  render={({ field }) => (
                    <Select
                      options={brandOptions}
                      styles={getCustomSelectStyles(isDarkMode, !!errors.brand_id)}
                      placeholder={allBrandsLoading ? t("devices.placeholders.loadingBrands") : t("devices.placeholders.selectBrand")}
                      value={brandOptions.find((opt) => opt.value === field.value) || null}
                      onChange={(option: any) => field.onChange(option?.value || "")}
                      isSearchable
                      isClearable
                      isLoading={allBrandsLoading}
                      isDisabled={isDisabled || allBrandsLoading}
                      noOptionsMessage={() => t("devices.noOptions.brands")}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                {errors.brand_id && (
                  <span className={errorClassName}>{errors.brand_id.message}</span>
                )}
              </div>
            )}
          </div>

          {/* Cuadro de información del Brand seleccionado */}
          {selectedBrand && (
            <BrandModel isDarkMode={isDarkMode} themeClasses={themeClasses} t={t as TFunction<"translation", undefined>} selectedBrand={selectedBrand} />
          )}

          {/* Sección: Información de Red */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-2 mt-4">
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("devices.sections.simConnectivity")}
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* SIM Card */}
            {isVisible("sim_card") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.simCard")} {isRequired("sim_card") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.sim_card)}
                  type="text"
                  placeholder={t("devices.placeholders.simCard")}
                  {...register("sim_card", {
                    required: isRequired("sim_card") ? t("devices.validation.simCardRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.sim_card && (
                  <span className={errorClassName}>{errors.sim_card.message}</span>
                )}
              </div>
            )}

            {/* ICCID */}
            {isVisible("iccid") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.iccid")} {isRequired("iccid") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.iccid)}
                  type="text"
                  placeholder={t("devices.placeholders.iccid")}
                  {...register("iccid", {
                    required: isRequired("iccid") ? t("devices.validation.iccidRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.iccid && (
                  <span className={errorClassName}>{errors.iccid.message}</span>
                )}
              </div>
            )}

            {/* Teléfono */}
            {isVisible("phone_number") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.phone")} {isRequired("phone_number") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.phone_number)}
                  type="text"
                  placeholder={t("devices.placeholders.phone")}
                  {...register("phone_number", {
                    required: isRequired("phone_number") ? t("devices.validation.phoneRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.phone_number && (
                  <span className={errorClassName}>{errors.phone_number.message}</span>
                )}
              </div>
            )}
          </div>

          {/* Sección: Fechas */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-2 mt-4">
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("devices.sections.dates")}
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Fecha de Instalación */}
            {isVisible("installation_date") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.installationDate")} {isRequired("installation_date") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.installation_date)}
                  type="datetime-local"
                  {...register("installation_date", {
                    required: isRequired("installation_date") ? t("devices.validation.installationDateRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.installation_date && (
                  <span className={errorClassName}>{errors.installation_date.message}</span>
                )}
              </div>
            )}

            {/* Fecha de Activación */}
            {isVisible("activation_date") && (
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                  {t("devices.fields.activationDate")} {isRequired("activation_date") && <span className="text-red-500">*</span>}
                </span>
                <input
                  className={getInputClassName(!!errors.activation_date)}
                  type="datetime-local"
                  {...register("activation_date", {
                    required: isRequired("activation_date") ? t("devices.validation.activationDateRequired") : false,
                  })}
                  disabled={isDisabled}
                />
                {errors.activation_date && (
                  <span className={errorClassName}>{errors.activation_date.message}</span>
                )}
              </div>
            )}
          </div>

          {/* Sección: Sensor de combustible (opcional). Para equipos que mandan el
              nivel en unidades crudas (157, 6500) y no en %: con el valor de
              tanque vacío y de tanque lleno se convierte a %. */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-2 mb-2 mt-4">
            <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
              Sensor de combustible <span className={`font-normal ${themeClasses.textSecondary}`}>(opcional)</span>
            </h3>
            <p className={`text-xs mt-1 ${themeClasses.textSecondary}`}>
              Sólo si el equipo manda el nivel en valores crudos y no en %. Déjalo vacío si no aplica.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col">
              <label htmlFor="fuel_sensor_param" className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                Parámetro
              </label>
              <input
                id="fuel_sensor_param"
                className={`${getInputClassName(!!errors.fuel_sensor?.param)} font-mono`}
                placeholder="can_fuel_level1"
                autoComplete="off"
                spellCheck={false}
                {...register("fuel_sensor.param", {
                  setValueAs: (v) => (typeof v === "string" ? v.trim() : v) || undefined,
                  validate: () => fuelSensorErrors(getValues("fuel_sensor")).param ?? true,
                })}
                disabled={isDisabled}
              />
              {errors.fuel_sensor?.param ? (
                <span className={errorClassName}>{errors.fuel_sensor.param.message}</span>
              ) : (
                fuelParam && (
                  <span className={`text-xs mt-1 ${themeClasses.textSecondary}`}>
                    {lastRaw !== undefined ? <>Último valor: <span className="font-mono">{String(lastRaw)}</span></> : "Sin lectura reciente de ese parámetro"}
                  </span>
                )
              )}
            </div>
            <div className="flex flex-col">
              <label htmlFor="fuel_sensor_empty" className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                Valor con tanque vacío
              </label>
              <input
                id="fuel_sensor_empty"
                type="number"
                step="any"
                inputMode="decimal"
                className={getInputClassName(!!errors.fuel_sensor?.empty_raw)}
                {...register("fuel_sensor.empty_raw", {
                  setValueAs: rawValue,
                  validate: () => fuelSensorErrors(getValues("fuel_sensor")).empty_raw ?? true,
                })}
                disabled={isDisabled}
              />
              {errors.fuel_sensor?.empty_raw && <span className={errorClassName}>{errors.fuel_sensor.empty_raw.message}</span>}
            </div>
            <div className="flex flex-col">
              <label htmlFor="fuel_sensor_full" className={`text-sm font-medium ${themeClasses.text} mb-1`}>
                Valor con tanque lleno
              </label>
              <input
                id="fuel_sensor_full"
                type="number"
                step="any"
                inputMode="decimal"
                className={getInputClassName(!!errors.fuel_sensor?.full_raw)}
                {...register("fuel_sensor.full_raw", {
                  setValueAs: rawValue,
                  validate: () => fuelSensorErrors(getValues("fuel_sensor")).full_raw ?? true,
                })}
                disabled={isDisabled}
              />
              {errors.fuel_sensor?.full_raw && <span className={errorClassName}>{errors.fuel_sensor.full_raw.message}</span>}
            </div>
          </div>

          {/* Mismo candado de hardware que el sensor. Tres estados (utils/panicButton). */}
          <div className={`mt-4 flex flex-col gap-1 text-sm ${themeClasses.text} ${isDisabled ? "opacity-60" : ""}`}>
            <label className="font-medium">
              ¿Tiene botón de pánico?
              <select
                className={getInputClassName(false)}
                {...register("has_panic_button", { setValueAs: parsePanicButton })}
                disabled={isDisabled}
              >
                {PANIC_BUTTON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className={`block text-xs ${themeClasses.textSecondary}`}>
                Sí: su código 5 cuenta como pánico en los informes. No: tampoco alerta en vivo.
                Sin revisar: alerta en vivo, pero no cuenta en los informes.
              </span>
            </label>
          </div>
        </form>
      }
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("devices.modal.editTitle")}
      buttonAction={
        <CRUDTooltip>
          <button
            type="submit"
            form="device-edit-form"
            disabled={isSubmitting || disableCRUD}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isSubmitting ? t("devices.actions.saving") : t("devices.actions.save")}
          </button>
        </CRUDTooltip>
      }
    />
  );
}
